import React from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { DEFAULT_BOILERPLATE_CODES } from "../../../../assets/boilerplateConstants";

export default function AddCodingTopic({ formData, setFormData }) {
  const handleConstraintChange = (index, value) => {
    const newConstraints = [...formData.constraints];
    newConstraints[index] = value;
    setFormData((prev) => ({
      ...prev,
      constraints: newConstraints,
    }));
  };

  const addConstraint = () => {
    setFormData((prev) => ({
      ...prev,
      constraints: [...prev.constraints, ""],
    }));
  };

  const removeConstraint = (index) => {
    setFormData((prev) => ({
      ...prev,
      constraints: prev.constraints.filter((_, i) => i !== index),
    }));
  };

  const handleExampleChange = (index, field, value) => {
    const newExamples = [...formData.examples];
    newExamples[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      examples: newExamples,
    }));
  };

  const addExample = () => {
    setFormData((prev) => ({
      ...prev,
      examples: [...prev.examples, { input: "", expected: "" }],
    }));
  };

  const removeExample = (index) => {
    setFormData((prev) => ({
      ...prev,
      examples: prev.examples.filter((_, i) => i !== index),
    }));
  };

  const handleVisibleTestCaseChange = (index, field, value) => {
    const newTestCases = [...formData.visibleTestCases];
    newTestCases[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      visibleTestCases: newTestCases,
    }));
  };

  const addVisibleTestCase = () => {
    setFormData((prev) => ({
      ...prev,
      visibleTestCases: [...prev.visibleTestCases, { input: "", expected: "" }],
    }));
  };

  const removeVisibleTestCase = (index) => {
    setFormData((prev) => ({
      ...prev,
      visibleTestCases: prev.visibleTestCases.filter((_, i) => i !== index),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleHiddenTestCaseChange = (index, field, value) => {
    const newTestCases = [...formData.hiddenTestCases];
    newTestCases[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      hiddenTestCases: newTestCases,
    }));
  };

  const addHiddenTestCase = () => {
    setFormData((prev) => ({
      ...prev,
      hiddenTestCases: [...prev.hiddenTestCases, { input: "", expected: "" }],
    }));
  };

  const removeHiddenTestCase = (index) => {
    setFormData((prev) => ({
      ...prev,
      hiddenTestCases: prev.hiddenTestCases.filter((_, i) => i !== index),
    }));
  };

  const handleBoilerplateChange = (lang, value) => {
    setFormData((prev) => ({
      ...prev,
      boilerplateCodes: {
        ...prev.boilerplateCodes,
        [lang]: value,
      },
    }));
  };

  return (
    <div className="space-y-6 border-t pt-8">
      <h3 className="text-lg font-semibold text-gray-900">Coding Problem</h3>


      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Question
        </label>
        <textarea
          name="question"
          value={formData.question}
          onChange={handleInputChange}
          placeholder="Brief question of this topic (optional)"
          rows="3"
          className="ca-input py-3"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Input Format
          </label>
          <textarea
            value={formData.inputFormat}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, inputFormat: e.target.value }))
            }
            placeholder="Describe input format..."
            rows="4"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Output Format
          </label>
          <textarea
            value={formData.outputFormat}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, outputFormat: e.target.value }))
            }
            placeholder="Describe output format..."
            rows="4"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Difficulty Level
        </label>
        <select
          value={formData.difficulty}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, difficulty: e.target.value }))
          }
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      {/* Constraints */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Constraints
        </label>
        {formData.constraints.map((constraint, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={constraint}
              onChange={(e) => handleConstraintChange(index, e.target.value)}
              placeholder={`Constraint ${index + 1}`}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formData.constraints.length > 1 && (
              <button
                type="button"
                onClick={() => removeConstraint(index)}
                className="px-3 py-2 text-red-600 hover:text-red-700"
              >
                <FaTrash />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addConstraint}
          className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          <FaPlus /> Add Constraint
        </button>
      </div>

      {/* Examples */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Examples
        </label>
        {formData.examples.map((ex, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <textarea
              value={ex.input}
              onChange={(e) => handleExampleChange(index, "input", e.target.value)}
              placeholder="Input (multiline supported)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            />
            <textarea
              value={ex.expected}
              onChange={(e) => handleExampleChange(index, "expected", e.target.value)}
              placeholder="Expected output (multiline supported)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            />
            {formData.examples.length > 1 && (
              <button
                type="button"
                onClick={() => removeExample(index)}
                className="px-3 py-2 text-red-600 hover:text-red-700"
              >
                <FaTrash />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addExample}
          className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          <FaPlus /> Add Example
        </button>
      </div>

      {/* Visible Test Cases */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Visible Test Cases
        </label>
        {formData.visibleTestCases.map((tc, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <textarea
              value={tc.input}
              onChange={(e) =>
                handleVisibleTestCaseChange(index, "input", e.target.value)
              }
              placeholder="Input (multiline supported)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            />
            <textarea
              value={tc.expected}
              onChange={(e) =>
                handleVisibleTestCaseChange(index, "expected", e.target.value)
              }
              placeholder="Expected output (multiline supported)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            />
            {formData.visibleTestCases.length > 1 && (
              <button
                type="button"
                onClick={() => removeVisibleTestCase(index)}
                className="px-3 py-2 text-red-600 hover:text-red-700"
              >
                <FaTrash />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addVisibleTestCase}
          className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          <FaPlus /> Add Visible Test Case
        </button>
      </div>

      {/* Hidden Test Cases */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Hidden Test Cases
        </label>
        {formData.hiddenTestCases.map((tc, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <textarea
              value={tc.input}
              onChange={(e) =>
                handleHiddenTestCaseChange(index, "input", e.target.value)
              }
              placeholder="Input (multiline supported)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            />
            <textarea
              value={tc.expected}
              onChange={(e) =>
                handleHiddenTestCaseChange(index, "expected", e.target.value)
              }
              placeholder="Expected output (multiline supported)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            />
            {formData.hiddenTestCases.length > 1 && (
              <button
                type="button"
                onClick={() => removeHiddenTestCase(index)}
                className="px-3 py-2 text-red-600 hover:text-red-700"
              >
                <FaTrash />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addHiddenTestCase}
          className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          <FaPlus /> Add Hidden Test Case
        </button>
      </div>

      {/* Boilerplate Codes */}
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Boilerplate Codes
        </label>
        {Object.keys(formData.boilerplateCodes).map((lang) => (
          <div key={lang}>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 capitalize">
                {lang}
              </label>
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    boilerplateCodes: {
                      ...prev.boilerplateCodes,
                      [lang]: DEFAULT_BOILERPLATE_CODES[lang] || "",
                    },
                  }));
                }}
                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Reset to Default
              </button>
            </div>
            <textarea
              value={formData.boilerplateCodes[lang]}
              onChange={(e) => handleBoilerplateChange(lang, e.target.value)}
              placeholder={`Starter code for ${lang}`}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
